/**
 * Wallet Configuration for SportsFuture
 * =====================================
 * Centralized configuration for EIP-6963 wallet discovery and WalletConnect.
 * Houses the Apertum chain definition, WalletConnect projectId, and
 * wallet display metadata.
 *
 * Pure web3 focused for SportsFuture (sign in/up + later signed submissions via Pinata/IPFS).
 *
 * SETUP:
 * 1. Register at https://cloud.walletconnect.com to get a projectId
 * 2. Project ID is set for sportsfuture.io; override with VITE_WALLETCONNECT_PROJECT_ID if needed
 *
 * @version 1.0.0 - Adapted from OpenPlaza for SportsFuture light UI
 */

// =============================================================================
// APERTUM CHAIN DEFINITION
// =============================================================================

export interface ChainConfig {
  chainId: number;
  chainIdHex: string;
  name: string;
  currency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  rpcUrls: string[];
  blockExplorerUrls: string[];
}

export const APERTUM_CHAIN: ChainConfig = {
  chainId: 2786,
  chainIdHex: '0xAE2',
  name: 'Apertum Mainnet',
  currency: {
    name: 'APTM',
    symbol: 'APTM',
    decimals: 18,
  },
  rpcUrls: ['https://rpc.apertum.io/ext/bc/YDJ1r9RMkewATmA7B35q1bdV18aywzmdiXwd9zGBq3uQjsCnn/rpc'],
  blockExplorerUrls: ['https://explorer.apertum.io'],
};

// =============================================================================
// ADD CHAIN PARAMS (used by useUnifiedWallet for network switching)
// =============================================================================

export function getAddChainParams() {
  return {
    chainId: APERTUM_CHAIN.chainIdHex,
    chainName: APERTUM_CHAIN.name,
    nativeCurrency: APERTUM_CHAIN.currency,
    rpcUrls: APERTUM_CHAIN.rpcUrls,
    blockExplorerUrls: APERTUM_CHAIN.blockExplorerUrls,
  };
}

/**
 * WalletConnect *required* namespace chain. Tangem (and many WC wallets) reject
 * pairing if 2786 is required because Apertum is not in their network list.
 * Avalanche C-Chain is widely listed; after pair we add/switch to Apertum and
 * pin the WC provider off 43114 (otherwise wallets ask for AVAX gas).
 */
export const WALLETCONNECT_PAIR_CHAIN: ChainConfig = {
  chainId: 43114,
  chainIdHex: '0xA86A',
  name: 'Avalanche C-Chain',
  currency: {
    name: 'Avalanche',
    symbol: 'AVAX',
    decimals: 18,
  },
  rpcUrls: ['https://api.avax.network/ext/bc/C/rpc'],
  blockExplorerUrls: ['https://snowtrace.io'],
};

// =============================================================================
// WALLETCONNECT CONFIGURATION
// =============================================================================

/**
 * Reown/WalletConnect Cloud project (sportsfuture.io allowlisted).
 * Override with VITE_WALLETCONNECT_PROJECT_ID if needed.
 */
export const WALLETCONNECT_PROJECT_ID: string =
  import.meta.env.VITE_WALLETCONNECT_PROJECT_ID ||
  'f9ab5b55a5cae29347721942927d26a2';

export function isWalletConnectConfigured(): boolean {
  const id = (WALLETCONNECT_PROJECT_ID || '').trim();
  if (!id || id === 'YOUR_PROJECT_ID_HERE') return false;
  return /^[a-f0-9]{32}$/i.test(id);
}

/**
 * WalletConnect metadata shown to users in wallet approval screens.
 */
export const WALLETCONNECT_METADATA = {
  name: 'SportsFuture',
  description: 'Tokenized Sports Future on Apertum Blockchain',
  url: 'https://sportsfuture.io',
  icons: ['https://sportsfuture.io/SFUT_icon_small.png'],
};

/** Page origin for WC metadata (localhost vs production). Avoids Reown URL mismatch. */
export function getWalletConnectMetadata(
  origin: string | undefined = typeof window !== 'undefined' ? window.location.origin : undefined
): typeof WALLETCONNECT_METADATA {
  const url = origin && origin.length > 0 ? origin : WALLETCONNECT_METADATA.url;
  return { ...WALLETCONNECT_METADATA, url };
}

// =============================================================================
// WALLET DISPLAY METADATA
// =============================================================================

/**
 * Known wallet RDNS identifiers mapped to display-friendly info.
 * EIP-6963 wallets announce themselves with an `rdns` field — this map
 * lets us show consistent branding even if the wallet's self-reported
 * name or icon is missing/ugly.
 */
export interface WalletBranding {
  displayName: string;
  priority: number; // Lower = shown first in the list
}

export const KNOWN_WALLETS: Record<string, WalletBranding> = {
  'io.metamask': {
    displayName: 'MetaMask',
    priority: 1,
  },
  'io.metamask.flask': {
    displayName: 'MetaMask Flask',
    priority: 2,
  },
  'app.core': {
    displayName: 'Core Wallet',
    priority: 3,
  },
  'com.trustwallet.app': {
    displayName: 'Trust Wallet',
    priority: 4,
  },
  'com.coinbase.wallet': {
    displayName: 'Coinbase Wallet',
    priority: 5,
  },
  'app.phantom': {
    displayName: 'Phantom',
    priority: 6,
  },
  'com.xovawallet': {
    displayName: 'Xova Wallet',
    priority: 7,
  },
  'io.rabby': {
    displayName: 'Rabby Wallet',
    priority: 8,
  },
  'com.brave.wallet': {
    displayName: 'Brave Wallet',
    priority: 9,
  },
  'com.okex.wallet': {
    displayName: 'OKX Wallet',
    priority: 10,
  },
  // Add more as needed
  'unknown.injected': {
    displayName: 'Browser Wallet',
    priority: 99,
  },
};

// Helper to get display name (for use in hooks)
export function getWalletDisplayName(rdns: string | null): string {
  if (!rdns) return 'Unknown Wallet';
  return KNOWN_WALLETS[rdns]?.displayName || rdns.split('.').pop() || 'Wallet';
}

export function getWalletPriority(rdns: string | null): number {
  if (!rdns) return 100;
  return KNOWN_WALLETS[rdns]?.priority ?? 50;
}

// Re-export chain for convenience
export { APERTUM_CHAIN as CHAIN };
